import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import AchievementsIcon from '../../../assets/homeRedesign/cards/achievements.png';
import DuelsIcon from '../../../assets/homeRedesign/cards/duels.png';
import LobbyIcon from '../../../assets/homeRedesign/cards/lobby.png';
import SocialIcon from '../../../assets/homeRedesign/cards/social.png';
import TournamentsIcon from '../../../assets/homeRedesign/cards/tournaments.png';
import VipIcon from '../../../assets/homeRedesign/cards/vip.png';
import {
	selectAchievementsLoaded,
	selectAchievementsTotalCount,
	selectAchievementsUnlockedCount,
} from '../../../store/achievements/achievementsReducer';
import { selectIsLogged } from '../../../store/auth/authReducer';
import { selectDuelRewardCounts, selectDuelsHub } from '../../../store/duels/duelsReducer';
import { RootStackParamList } from '../../navigator';
import { duelHomeCard, DuelHomeTarget } from '../Duels/duelHomeCard';

type Nav = NativeStackNavigationProp<RootStackParamList>;

export type HomeCard = {
	key: string;
	title: string;
	meta: string;
	icon: any;
	/** The two taller cards in the middle of each row. */
	featured?: boolean;
	onPress: () => void;
};

/**
 * The six home category cards, two rows of three with the featured card in the middle. The
 * mobile home and the desktop home draw the same six in the same order; only the frame differs.
 * The meta lines are still the design's mock copy, except the achievements count.
 */
export const buildHomeCards = (
	navigate: Nav['navigate'],
	t: TFunction,
	{
		isLogged,
		achievementsMeta,
		duels,
	}: {
		isLogged: boolean;
		achievementsMeta: string;
		duels: { meta: string; target: DuelHomeTarget };
	},
): HomeCard[] => [
	{
		key: 'tournaments',
		title: t('T_SITE_HOME_TOURNAMENTS', { defaultValue: 'TOURNAMENTS' }),
		meta: 'IN 04:12',
		icon: TournamentsIcon,
		onPress: () => navigate('Tournaments'),
	},
	{
		key: 'lobby',
		title: t('T_SITE_HOME_LOBBY', { defaultValue: 'LOBBY' }),
		meta: '42 TABLES',
		icon: LobbyIcon,
		featured: true,
		onPress: () => navigate('Lobby'),
	},
	{
		key: 'vip',
		title: t('T_SITE_VIP', { defaultValue: 'VIP' }),
		meta: 'LEVEL 2 - 60%',
		icon: VipIcon,
		onPress: () => navigate('VipRooms'),
	},
	{
		key: 'social',
		title: t('T_SITE_HOME_SOCIAL', { defaultValue: 'SOCIAL' }),
		meta: 'RED ACES',
		icon: SocialIcon,
		onPress: () => (isLogged ? navigate('Friends') : navigate('Login')),
	},
	{
		key: 'duels',
		title: t('T_SITE_MENU_DUELS', { defaultValue: 'Duels' }).toUpperCase(),
		meta: duels.meta,
		icon: DuelsIcon,
		featured: true,
		onPress: () => {
			if (!isLogged) navigate('Login');
			else if (duels.target === 'rewards')
				navigate('Inventory', { tab: 'inventory', category: 'rewards' });
			else if (duels.target === 'season') navigate('DuelSeason');
			else navigate('Duels');
		},
	},
	{
		key: 'achievements',
		title: t('T_SITE_HOME_ACHIEVEMENTS', { defaultValue: 'ACHIEVEMENTS' }),
		meta: achievementsMeta,
		icon: AchievementsIcon,
		onPress: () => (isLogged ? navigate('Achievements') : navigate('Login')),
	},
];

export const useHomeCards = (): HomeCard[] => {
	const { t } = useTranslation();
	const { navigate } = useNavigation<Nav>();
	const isLogged = useSelector(selectIsLogged);
	const achievementsLoaded = useSelector(selectAchievementsLoaded);
	const unlockedAchievements = useSelector(selectAchievementsUnlockedCount);
	const totalAchievements = useSelector(selectAchievementsTotalCount);
	const duelsHub = useSelector(selectDuelsHub);
	const duelCounts = useSelector(selectDuelRewardCounts);
	return buildHomeCards(navigate, t, {
		isLogged,
		achievementsMeta:
			isLogged && achievementsLoaded ? `${unlockedAchievements}/${totalAchievements}` : '',
		duels: isLogged ? duelHomeCard(t, duelsHub, duelCounts) : { meta: '', target: 'duels' },
	});
};
